import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { fishDataFromCsv as fishData } from '../data/fishDataFromCsv.js';
import EnhancedFishCard from '../components/EnhancedFishCard.js';
import { useFish } from '../hooks/useFish.js';

const Home = () => {
  const { fish: apiFish, loading } = useFish();
  const [currentFishData, setCurrentFishData] = useState([]);
  
  useEffect(() => {
    // Use API data if available, otherwise fallback to local data
    if (apiFish && apiFish.length > 0) {
      setCurrentFishData(apiFish);
    } else {
      // Load fish data from localStorage or fallback to default fishData
      const savedFish = localStorage.getItem('saltfishy_fish_inventory');
      if (savedFish) {
        try {
          const parsedFish = JSON.parse(savedFish);
          setCurrentFishData(parsedFish);
        } catch (error) {
          console.warn('Error loading fish data:', error);
          setCurrentFishData(fishData);
        }
      } else {
        setCurrentFishData(fishData);
      }
    }
  }, [apiFish]);

  const featuredFish = currentFishData.slice(0, 6);

  return (
    <div>
      {/* Modern Hero Section */}
      <section style={{
        backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.3)), url('/images/fish-7994570_1920.jpg')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Animated Background Elements */}
        <div style={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
          {/* Bubbles */}
          {[...Array(8)].map((_, i) => (
            <div
              key={i}
              className="animate-bubble"
              style={{
                position: 'absolute',
                width: '1rem',
                height: '1rem',
                borderRadius: '50%',
                background: 'rgba(255, 255, 255, 0.1)',
                left: `${10 + i * 12}%`,
                animationDelay: `${i * 1.5}s`,
                animationDuration: `${6 + i * 2}s`,
              }}
            />
          ))}
          
          {/* Wave overlay */}
          <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '8rem' }}>
            <svg
              viewBox="0 0 1440 120"
              style={{ width: '100%', height: '100%', fill: 'var(--background)' }}
              preserveAspectRatio="none"
            >
              <path d="M0,64 C480,128 960,0 1440,64 L1440,120 L0,120 Z" />
            </svg>
          </div>
        </div>

        {/* Content */}
        <div style={{ position: 'relative', zIndex: 10, maxWidth: '1200px', margin: '0 auto', padding: '0 1rem', textAlign: 'center' }}>
          <div style={{ maxWidth: '56rem', margin: '0 auto' }}>
            {/* Badge */}
            <div className="animate-fade-in" style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.5rem 1rem',
              borderRadius: '50px',
              background: 'rgba(255, 255, 255, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              backdropFilter: 'blur(4px)',
              marginBottom: '2rem'
            }}>
              <span style={{ fontSize: '1.5rem' }}>🌊</span>
              <span style={{ fontSize: '0.875rem', fontWeight: '500', color: 'white' }}>Premium Marine Fish from Maldives</span>
            </div>

            {/* Title */}
            <h1 className="font-display animate-fade-in" style={{
              fontSize: 'clamp(3rem, 8vw, 6rem)',
              fontWeight: 'bold',
              color: 'white',
              lineHeight: '1.1',
              marginBottom: '2rem',
              animationDelay: '0.2s'
            }}>
              SaltFishy
              <span style={{ display: 'block', color: 'var(--ocean-surface)' }}>Marine Paradise</span>
            </h1>

            {/* Subtitle */}
            <p className="animate-fade-in" style={{
              fontSize: '1.25rem',
              color: 'rgba(255, 255, 255, 0.8)',
              maxWidth: '42rem',
              margin: '0 auto 3rem auto',
              lineHeight: '1.6',
              animationDelay: '0.4s'
            }}>
              Discover the finest collection of exotic marine fish from the pristine waters of the Maldives. 
              Each fish is carefully selected and quarantined for your peace of mind.
            </p>

            {/* CTA Buttons */}
            <div className="animate-fade-in" style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '1rem',
              animationDelay: '0.6s'
            }}>
              <Link to="/shop">
                <button className="btn-hero" style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  padding: '1rem 2rem',
                  fontSize: '1.125rem'
                }}>
                  Explore Collection
                  <span>↓</span>
                </button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Fish Collection */}
      <section style={{ padding: '5rem 0', background: 'var(--background)' }}>
        <div className="container">
          {/* Section Header */}
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.5rem 1rem',
              borderRadius: '50px',
              background: 'hsl(200, 85%, 25%, 0.1)',
              color: 'var(--primary)',
              marginBottom: '1rem'
            }}>
              <span style={{ fontSize: '1.5rem' }}>🐠</span>
              <span style={{ fontSize: '0.875rem', fontWeight: '500' }}>Our Collection</span>
            </div>
            <h2 className="font-display" style={{
              fontSize: 'clamp(2.25rem, 5vw, 3rem)',
              fontWeight: 'bold',
              color: 'var(--foreground)',
              marginBottom: '1rem'
            }}>
              Premium Marine Fish
            </h2>
            <p style={{
              color: 'var(--muted-foreground)',
              maxWidth: '42rem',
              margin: '0 auto',
              lineHeight: '1.6'
            }}>
              Browse our carefully curated collection of exotic marine fish from the pristine waters of the Maldives. 
              Each fish is quarantined and acclimated for your aquarium.
            </p>
          </div>

          {/* Fish Grid */}
          {featuredFish.length > 0 ? (
            <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '1.5rem' }}>
              {featuredFish.map((fish, index) => (
                <div 
                  key={fish.id} 
                  className="animate-fade-in"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <EnhancedFishCard fish={fish} />
                </div>
              ))}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '5rem 0' }}>
              <span style={{ fontSize: '4rem', display: 'block', marginBottom: '1rem' }}>🐠</span>
              <h3 className="font-display" style={{
                fontSize: '1.5rem',
                fontWeight: '600',
                color: 'var(--foreground)',
                marginBottom: '0.5rem'
              }}>
                Loading our collection...
              </h3>
              <p style={{ color: 'var(--muted-foreground)' }}>
                Please wait while we load our beautiful marine fish
              </p>
            </div>
          )}

          {/* View All Link */}
          {featuredFish.length > 0 && (
            <div style={{ textAlign: 'center', marginTop: '3rem' }}>
              <Link to="/shop">
                <button className="btn-hero" style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  View All {currentFishData.length} Fish
                  <span>→</span>
                </button>
              </Link>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Home;
